import { useEffect, useState } from 'react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { EmailPreviewModal, usePreviewModal } from '@frontend/email-preview-modal';
import { EmailThumbnail } from '@frontend/email-thumbnail';
import { useTranslation } from '@frontend/i18n';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, Stepper, Text } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../../../bulk-message-editor.context';
import { stepValueContainerStyles } from '../styles';
import { Step, StepId } from '../types';

type UseEmailContentStepParams = {
  contentError: string;
  next: (stepId: StepId) => void;
  openNewComposer: () => void;
  openEditComposer: () => void;
  previous: (stepId: StepId) => void;
};

export const useEmailContentStep = ({
  contentError,
  next,
  openEditComposer,
  openNewComposer,
  previous,
}: UseEmailContentStepParams): Step => {
  const { t } = useTranslation('bulk-messaging');
  const { messageTemplate, subject, templateJson, thumbnailMediaId, thumbnailUrl } = useBulkMessageEditorContext();

  const [previewHtml, setPreviewHtml] = useState<string>();
  const { deviceSizeProps, generatePreview, previewModalProps, transformedText } = usePreviewModal(
    previewHtml,
    TemplateType_Slug.BULK_MESSAGE
  );

  const handlePreviewModalOpen = () => setPreviewHtml(messageTemplate);

  useEffect(() => {
    if (transformedText) {
      generatePreview();
    }
  }, [previewHtml, transformedText]);

  useEffect(() => {
    if (!previewModalProps.show) {
      setPreviewHtml(undefined);
    }
  }, [previewModalProps.show]);

  const id: StepId = 'content';
  const src = thumbnailMediaId ? thumbnailMediaId : thumbnailUrl;

  return {
    id,
    Component: (
      <>
        <Stepper.Title>{t('Email Content')}</Stepper.Title>

        <Stepper.Content>
          <Text size='medium' css={{ marginBottom: theme.spacing(2) }}>
            {t('Build or use a template to construct your email.')}
          </Text>
          {!!(subject || templateJson) ? (
            <>
              <EmailThumbnail
                src={src}
                onClick={handlePreviewModalOpen}
                css={{ marginBottom: theme.spacing(2) }}
                width={200}
                height={200}
                alt={t('Preview Email')}
              />
              {contentError && (
                <Text size='small' color='error' css={{ marginBottom: theme.spacing(1) }}>
                  {contentError}
                </Text>
              )}
              <div css={{ display: 'flex', columnGap: theme.spacing(1), marginTop: theme.spacing(3) }}>
                <Button onClick={openEditComposer} trackingId={`${BulkEmailPrefixes.Editor}-edit-email-btn`}>
                  {t('Edit')}
                </Button>
                <Button
                  variant='secondary'
                  onClick={openNewComposer}
                  trackingId={`${BulkEmailPrefixes.Editor}-compose-email-btn`}
                >
                  {t('Start New')}
                </Button>
              </div>
            </>
          ) : (
            <>
              {contentError && (
                <Text size='small' color='error' css={{ marginBottom: theme.spacing(1) }}>
                  {contentError}
                </Text>
              )}
              <Button
                onClick={openNewComposer}
                css={{ marginTop: theme.spacing(2) }}
                trackingId={`${BulkEmailPrefixes.Editor}-compose-email-btn`}
              >
                {t('Start New')}
              </Button>
            </>
          )}
        </Stepper.Content>

        <Stepper.ButtonBarAlternate>
          <Stepper.ButtonAlternate onClick={() => previous(id)} position='secondary' type='none'>
            {t('Previous')}
          </Stepper.ButtonAlternate>
          <Stepper.ButtonAlternate onClick={() => next(id)} position='primary' type='none'>
            {t('Next')}
          </Stepper.ButtonAlternate>
        </Stepper.ButtonBarAlternate>
        {previewModalProps.show && (
          <EmailPreviewModal
            deviceSizeProps={deviceSizeProps}
            modalProps={previewModalProps}
            previewHtml={transformedText}
            setPreviewHtml={setPreviewHtml}
            trackingIdPrefix={BulkEmailPrefixes.Editor}
          />
        )}
      </>
    ),
    collapsedValue: (
      <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
        <Text>{t('Email Content')}</Text>
        {thumbnailMediaId || thumbnailUrl ? (
          <EmailThumbnail src={src} width={200} height={200} alt={t('Edit Email')} />
        ) : (
          <EmailThumbnail />
        )}
      </div>
    ),
    readValue: (
      <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
        <Text>{t('Email Content')}</Text>
        <EmailThumbnail src={src} onClick={handlePreviewModalOpen} width={200} height={200} alt={t('Edit Email')} />
        <div css={{ display: 'flex', columnGap: theme.spacing(1) }}>
          <Button variant='secondary' onClick={handlePreviewModalOpen}>
            {t('View')}
          </Button>
        </div>
        {previewModalProps.show && (
          <EmailPreviewModal
            deviceSizeProps={deviceSizeProps}
            modalProps={previewModalProps}
            previewHtml={transformedText}
            setPreviewHtml={setPreviewHtml}
            trackingIdPrefix={BulkEmailPrefixes.Editor}
          />
        )}
      </div>
    ),
  };
};
